import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Link as MuiLink,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import toast from 'react-hot-toast';
import { useCapsLockDetector } from '../hooks/useCapsLockDetector';
import { useResetPasswordMutation } from '../authApiSlice';

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .required('Password is required')
    .matches(/(?=.*[A-Z])/, 'Please provide strong password')
    .matches(/(?=.*[0-9])/, 'Please provide strong password')
    .matches(/(?=.*[!@#$%^&*])/, 'Please provide strong password'),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});
const ResetPasswordForm = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isCapsLockOn = useCapsLockDetector();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetPassword] = useResetPasswordMutation();
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (!token) {
        throw new Error('Reset token not found');
      }
      const { password, confirmPassword } = values;
      const promise = resetPassword({
        token,
        password,
        confirmPassword,
      }).unwrap();

      toast.promise(promise, {
        loading: 'Resetting password...',
        success: 'Password reset successful! Redirecting to login...',
        error: (err) => {
          resetForm();
          if (err.data && err.data.message) {
            return err.data.message;
          }
          return 'Error resetting password. Please try again.';
        },
      });

      await promise;
      setTimeout(() => {
        navigate('/');
      }, 3000);
    },
  });

  const fields = [
    {
      name: 'password',
      label: 'New Password *',
      type: showPassword ? 'text' : 'password',
      endAdornment: (
        <InputAdornment position='end'>
          <IconButton onClick={() => setShowPassword((prev) => !prev)} edge='end'>
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    },
    {
      name: 'confirmPassword',
      label: 'Confirm Password *',
      type: showConfirmPassword ? 'text' : 'password',
      endAdornment: (
        <InputAdornment position='end'>
          <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge='end'>
            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      ),
    },
  ];

  return (
    <Box>
      <Box flexDirection='column' display='flex' alignItems='center'>
        <LockResetOutlinedIcon
          sx={{
            color: 'text.secondary',
            height: '10vh',
            width: '10vw',
            opacity: '0.8',
          }}
        />
      </Box>
      <Box mb={isSmallScreen ? 0 : 0}>
        <Typography align='center' variant='h5'>
          Reset Password
        </Typography>
        <Typography align='center' color='text.secondary'>
          Create a strong password
        </Typography>
      </Box>
      <Box textAlign={'center'}>
        <form onSubmit={formik.handleSubmit}>
          {fields.map((field) => (
            <TextField
              key={field.name}
              margin='normal'
              label={field.label}
              name={field.name}
              type={field.type}
              variant='outlined'
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched[field.name] && !!formik.errors[field.name]}
              helperText={formik.touched[field.name] && formik.errors[field.name]}
              InputProps={{ endAdornment: field.endAdornment }}
              sx={{
                '& .MuiInputLabel-root': {
                  fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
                },
              }}
            />
          ))}
          {isCapsLockOn && (
            <Typography variant='body2' color='gray'>
              Caps Lock is on
            </Typography>
          )}
          <Box>
            <Button
              type='submit'
              variant='contained'
              size='large'
              sx={{
                marginTop: 1,
                marginBottom: 1,
              }}
            >
              Submit
            </Button>
          </Box>
          <MuiLink
            component={Link}
            to='/auth'
            underline='none'
            sx={{
              color: 'grey',
              cursor: 'default',
              '&:hover': {
                color: theme.palette.primary.main,
                textDecoration: 'underline',
              },
            }}
          >
            Back to Login
          </MuiLink>
        </form>
      </Box>
    </Box>
  );
};

export default ResetPasswordForm;
